// Aesthetics score over a plain Pog.
//
// The hierarchy comes from the SAVED CONTRACT when the caller has one, and otherwise from the
// plan's own CDT columns. It is never inferred from the layout being graded: an inference picks
// the hierarchy that best explains the shelves, so a damaged plan would be graded against the
// hierarchy that flatters its damage.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import {
  pogShelfRowResolver,
  pogToStudioItems,
  type PogAdapterOptions,
  type PogShelfRestriction,
} from "../adapters/pogToStudioItems";
import {
  orientOf,
  type AestheticsRules,
  type BlockingAxis,
  type SavedAestheticsRules,
} from "./aestheticsRules";
import {
  scorePlanogramAesthetics,
  type AestheticsScope,
  type AestheticsScore,
  type BandRule,
  type BandSource,
  type CoreRange,
} from "./aestheticsScore";
import {
  namesNoBlock,
  type LengthUnit,
  type SpineLevel,
  type StudioDraft,
  type StudioItem,
} from "./studioTypes";

/** The CDT a store plan was generated against, coarse to fine: CDT0 is the variant. */
export const POG_CDT_COLUMNS = ["variant", "cdt1", "cdt2", "cdt3"] as const;

const blockValue = (item: StudioItem, attr: string): string | null => {
  const value = item.attrs[attr]?.trim() ?? "";
  return namesNoBlock(value) ? null : value;
};

/**
 * The levels a plan's blocks are read at, from its CDT columns alone.
 *
 * A column is a level when it groups products: at least half of them carry a real value, it has
 * two or more values, and its blocks are not one product each (then it is the order inside a
 * block, not a block). A column that splits nothing its parent did not already split repeats the
 * level above and is skipped. The first column left over below the last level is the leaf: its
 * rank orders the products inside the smallest blocks.
 */
export const hierarchyFromColumns = (
  items: readonly StudioItem[],
  columns: readonly string[] = POG_CDT_COLUMNS,
): { levels: string[]; leafAttr: string | undefined } => {
  const levels: string[] = [];
  let previousBlocks = 1;
  for (const attr of columns) {
    const carried = items.filter((item) => blockValue(item, attr) !== null).length;
    const values = new Set(items.map((item) => blockValue(item, attr)).filter(Boolean)).size;
    const blocks = new Set(
      items.map((item) =>
        [...levels, attr].map((level) => blockValue(item, level) ?? "—").join("␟"),
      ),
    ).size;
    const informative =
      carried >= items.length / 2 &&
      values >= 2 &&
      blocks > previousBlocks &&
      blocks <= items.length / 2;
    if (informative) {
      levels.push(attr);
      previousBlocks = blocks;
    }
  }
  return { levels, leafAttr: leafBelow(items, columns, levels) };
};

/* The leaf lies BELOW the last level: a column skipped for repeating its parent says nothing
   about the order inside a block that is finer than it. */
const leafBelow = (
  items: readonly StudioItem[],
  columns: readonly string[],
  levels: readonly string[],
): string | undefined => {
  const last = columns.indexOf(levels[levels.length - 1]);
  return columns
    .slice(last + 1)
    .find((attr) => new Set(items.map((item) => blockValue(item, attr)).filter(Boolean)).size >= 2);
};

/**
 * Roles on a column hierarchy, by name. The spine resolver fills roles by position, which on a
 * spine of CDT levels plus size makes size the brand of a one-level plan and the flavour of every
 * plan. The brand is the plan's own brand field; the flavour is the leaf below the last level.
 */
const columnRoles = (
  items: readonly StudioItem[],
  columns: { levels: readonly string[]; leafAttr: string | undefined },
): Pick<AestheticsRules, "brandAttr" | "flavourAttr"> => {
  const brandCarried =
    items.filter((item) => blockValue(item, "brand") !== null).length >= items.length / 2;
  return {
    brandAttr: brandCarried ? "brand" : columns.levels.includes("cdt1") ? "cdt1" : "",
    flavourAttr: columns.leafAttr ?? "",
  };
};

/**
 * One shelf's variant restrictions exactly as a store's generation sent them to the pogger:
 * an entry of the request's `fixtures[].shelves[]`, addressed the way the request addresses it.
 * Every value is a variant (CDT0) value, the column the pogger enforces them on.
 */
export interface SentShelfRestriction {
  /** The request's `bay_no`, 1-based. */
  bay: number;
  /** The request's `shelf_no`: 1 = the top shelf of that bay. */
  shelf: number;
  /** The request's `fixtureId`, matched to the pog shelf's `uniqueId` before bay and shelf. */
  fixtureId?: string;
  /** `variants_restrictions.must_not_have`: no facing of these variants on this shelf. */
  mustNotHave: readonly string[];
  /** `variants_restrictions.must_have`: at least one facing of each on this shelf. */
  mustHave: readonly string[];
  /** `variants_restrictions.may_have`: "*" (the pogger's default) allows every variant; a list
   *  allows only those, so every other variant is fenced off this shelf. */
  mayHave: "*" | readonly string[];
}

/** The restrictions a store pog's solve was given, and where they were read. */
export interface SentRestrictions {
  /** "request": the generation request (`output_store_pog.pogger_request_path`).
   *  "emission": the CDT emission stored beside it (`*.store-emission.json`, its `rows`). */
  source: Extract<BandSource, "request" | "emission">;
  shelves: readonly SentShelfRestriction[];
}

export interface PogAestheticsOptions {
  /** The saved draft this category ships. Given, the plan is graded against it. */
  draft?: StudioDraft;
  /** Just the level order, when the caller has the tree but not a whole draft. */
  spine?: readonly SpineLevel[];
  /** Names the plan in the warning when it cannot be scored. */
  planId?: string | number;
  /** How the plan's items are read, e.g. with the category's item-master overlay. */
  adapter?: PogAdapterOptions;
  /** The columns the hierarchy is read from, coarse to fine; the plan's CDT columns by default.
   *  A category whose hierarchy lives in an item-master overlay names the overlay's columns. */
  columns?: readonly string[];
  /**
   * The restrictions the store's solve was sent. Given (even with no shelf restricted), the
   * bands are graded against these and the fences stored on the pog are not read: those can be
   * stale category-wide groups the generation never carried. Omitted or null, the pog's stored
   * fences are the fallback and the result reports `bandSource: "legacy fences"`.
   */
  restrictions?: SentRestrictions | null;
  /**
   * The store's core range as its generation request sent it (`coreRangeFromRequest`). The
   * unplaced check counts only these products; the rest of the range may be left off. Omitted
   * or null, the products the pog flags `inCoreRange` stand in and the result reports
   * `coreRangeSource: "pog flag"`.
   */
  coreRange?: CoreRange | null;
  /** The wall's blocking, for a plan graded on its own columns: "horizontal" grades every level
   *  as a band. A draft or spine names each level's orientation itself and wins. */
  blocking?: BlockingAxis | null;
}

const stringsOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
    : [];

const positive = (value: unknown): number | null => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 ? Math.round(number) : null;
};

/**
 * The restrictions a generation request sent, read off the request JSON the backend stores for
 * a store pog (`pogger_request_path`): `fixtures[].bay_no`, `fixtures[].shelves[].shelf_no`,
 * `fixtureId` and `variants_restrictions` ({ must_have, must_not_have, may_have }). Null when
 * the value is not a pogger request.
 */
export const sentRestrictionsFromRequest = (request: unknown): SentRestrictions | null => {
  const fixtures = (request as { fixtures?: unknown } | null)?.fixtures;
  if (!Array.isArray(fixtures)) return null;
  const shelves: SentShelfRestriction[] = [];
  for (const fixture of fixtures) {
    const bay = positive((fixture as { bay_no?: unknown } | null)?.bay_no);
    const list = (fixture as { shelves?: unknown } | null)?.shelves;
    if (bay === null || !Array.isArray(list)) continue;
    for (const entry of list) {
      const shelf = positive((entry as { shelf_no?: unknown } | null)?.shelf_no);
      if (shelf === null) continue;
      const { variants_restrictions: restrictions, fixtureId } = entry as {
        variants_restrictions?: Record<string, unknown> | null;
        fixtureId?: unknown;
      };
      const mayHave = restrictions?.may_have;
      shelves.push({
        bay,
        shelf,
        ...(typeof fixtureId === "string" && fixtureId ? { fixtureId } : {}),
        mustNotHave: stringsOf(restrictions?.must_not_have),
        mustHave: stringsOf(restrictions?.must_have),
        mayHave: Array.isArray(mayHave) ? stringsOf(mayHave) : "*",
      });
    }
  }
  return { source: "request", shelves };
};

/** Twin id → tray id from `either_or_items` `[tray, "<tray>-UNIT"]` pairs: a placed twin is stored
 *  under its tray's code, so the two are one range item. */
const twinTrays = (request: unknown): Map<string, string> => {
  const trays = new Map<string, string>();
  if (typeof request !== "object" || request === null || !("either_or_items" in request)) {
    return trays;
  }
  const pairs: unknown = request.either_or_items;
  if (!Array.isArray(pairs)) return trays;
  for (const pair of pairs) {
    if (!Array.isArray(pair)) continue;
    const tray: unknown = pair[0];
    const twin: unknown = pair[1];
    if (typeof tray === "string" && twin === `${tray}-UNIT`) trays.set(twin, tray);
  }
  return trays;
};

/**
 * The core range a generation request sent: the `required_items` of
 * `min_distribution.store_range_items` and of `min_distribution.core_range_items`, the
 * products the solve was told it must place. Null when the request carries no
 * `min_distribution` (then nothing says what is core, and the pog's flags stand in). A request
 * whose lists are empty names an empty core range: nothing is core, so the check grades nothing.
 */
export const coreRangeFromRequest = (request: unknown): CoreRange | null => {
  const distribution = (request as { min_distribution?: unknown } | null)?.min_distribution;
  if (!distribution || typeof distribution !== "object") return null;
  const listOf = (key: string) =>
    stringsOf(
      (distribution as Record<string, { required_items?: unknown } | null | undefined>)[key]
        ?.required_items,
    );
  const trays = twinTrays(request);
  const skuIds = [
    ...new Set(
      [...listOf("store_range_items"), ...listOf("core_range_items")].map(
        (id) => trays.get(id) ?? id,
      ),
    ),
  ];
  return { source: "request", skuIds };
};

/** The products the pog itself flags core range, placed or not. */
const coreRangeFromPog = (pog: Pog): CoreRange => {
  const skuIds = new Set<string>();
  for (const bay of pog.planogram.bays)
    for (const shelf of bay.shelves)
      for (const item of shelf.items) if (item.inCoreRange) skuIds.add(item.productCode || item.id);
  for (const item of pog.unrangedItems)
    if (item.inCoreRange) skuIds.add(item.productCode || item.id);
  return { source: "pog flag", skuIds: [...skuIds] };
};

/**
 * The blocking a generation request asked the pogger for: vertical when either
 * `verticalBlockingMode` (`optimization_options`, `mudmap_generation_options`) is true, as
 * owa-backend's `verticalBlockingModeOf` sends it; horizontal when one is false or the mudmap
 * asks for `horizontalRectangles`. Null when it says neither.
 */
export const blockingFromRequest = (request: unknown): BlockingAxis | null => {
  const optimization = fieldOf(request, "optimization_options");
  const mudmap = fieldOf(request, "mudmap_generation_options");
  if (!isObject(optimization) && !isObject(mudmap)) {
    // A request with neither options block is not one this reads: say so, rather than grade
    // the plan as if it had asked for no blocking.
    console.warn("Generation request has no optimization or mudmap options; blocking unknown.");
    return null;
  }
  const modes = [
    fieldOf(optimization, "verticalBlockingMode"),
    fieldOf(mudmap, "verticalBlockingMode"),
  ];
  if (modes.includes(true)) return "vertical";
  if (modes.includes(false) || fieldOf(mudmap, "horizontalRectangles") === true)
    return "horizontal";
  return null;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const fieldOf = (value: unknown, key: string): unknown =>
  isObject(value) ? value[key] : undefined;

/** "2 | 3" → [2, 3]: the emission's bay and shelf cells are pipe-joined lists. */
const numbersOf = (value: unknown): number[] =>
  String(value ?? "")
    .split("|")
    .map((part) => positive(part.trim()))
    .filter((number): number is number => number !== null);

/**
 * The restrictions the CDT materializer emitted for a store, read off the emission the backend
 * stores beside the request (`*.store-emission.json`): `rows[]` of { variant, bay_no, shelf,
 * condition }, bays and shelves pipe-joined. MUST_HAVE_ONLY reads as MUST_HAVE plus MAY_HAVE,
 * as the backend turns it into the request: that variant must show and nothing else may. The request is the better source: it also carries
 * whatever was merged in after the emission. Null when the value is not an emission.
 */
export const sentRestrictionsFromEmission = (emission: unknown): SentRestrictions | null => {
  const rows = (emission as { rows?: unknown } | null)?.rows;
  if (!Array.isArray(rows)) return null;
  const byShelf = new Map<
    string,
    { bay: number; shelf: number; mustNotHave: string[]; mustHave: string[]; mayHave: string[] }
  >();
  for (const row of rows) {
    const {
      variant,
      condition,
      bay_no: bays,
      shelf: shelves,
    } = (row ?? {}) as {
      variant?: unknown;
      condition?: unknown;
      bay_no?: unknown;
      shelf?: unknown;
    };
    if (typeof variant !== "string" || !variant.trim()) continue;
    const kind = String(condition ?? "").toUpperCase();
    for (const bay of numbersOf(bays)) {
      for (const shelf of numbersOf(shelves)) {
        const key = `${bay}|${shelf}`;
        const cell = byShelf.get(key) ?? {
          bay,
          shelf,
          mustNotHave: [],
          mustHave: [],
          mayHave: [],
        };
        if (kind === "MUST_NOT_HAVE") cell.mustNotHave.push(variant);
        else if (kind === "MUST_HAVE") cell.mustHave.push(variant);
        else if (kind === "MAY_HAVE") cell.mayHave.push(variant);
        else if (kind === "MUST_HAVE_ONLY") {
          // As the backend sends it (toAlgoVariantRestrictionTypes): MUST_HAVE plus MAY_HAVE, so
          // the shelf's may_have list narrows to it and every other variant is fenced off.
          cell.mustHave.push(variant);
          cell.mayHave.push(variant);
        }
        byShelf.set(key, cell);
      }
    }
  }
  return {
    source: "emission",
    shelves: [...byShelf.values()].map((cell) => ({
      bay: cell.bay,
      shelf: cell.shelf,
      mustNotHave: cell.mustNotHave,
      mustHave: cell.mustHave,
      mayHave: cell.mayHave.length ? cell.mayHave : "*",
    })),
  };
};

/**
 * The spine that grades a store pog against its CDT release: the release's compiled levels,
 * already named by the pog column each lands in (variant, cdt1, cdt2, cdt3) and carrying the
 * merchant's orientation, plus size, as the score appends size to a plan's own columns.
 * Without size the score would read "Size" in the __cdt3 label as the size level and stop
 * grading cdt3 as a block. No levels, no spine: the plan is graded on its own columns.
 */
export const spineOfRelease = (levels: readonly SpineLevel[]): readonly SpineLevel[] | null =>
  levels.length ? [...levels, { attr: "size", label: "Size", orient: "horizontal" }] : null;

/**
 * What a stored store-pog score is graded against, as the backend reads it: the CDT release's
 * `spine` (raw, before `spineOfRelease`), and the restrictions and core range the store's
 * generation request sent (`sentRestrictionsFromRequest`, `coreRangeFromRequest`). Each one
 * missing falls back as `PogAestheticsOptions` says.
 */
export interface AestheticsGrading {
  spine?: readonly SpineLevel[] | null;
  /** The id of the release `spine` is from (null: the plan has no release): a stored score
   *  records it, so a reader can tell whether it was graded against the plan's release now. */
  releaseId?: number | null;
  restrictions?: SentRestrictions | null;
  coreRange?: CoreRange | null;
  /** The blocking the store's request sent (`blockingFromRequest`): read only when the release
   *  has no levels to orient the plan's columns. */
  blocking?: BlockingAxis | null;
  /** The category's length unit, for the score's words only (the measures are bay shares). */
  lengthUnit?: LengthUnit;
}

/** A bare spine array is the release spine alone (the older `calculateMetrics` argument). */
export const gradingOf = (
  grading: readonly SpineLevel[] | AestheticsGrading | null | undefined,
): AestheticsGrading =>
  Array.isArray(grading)
    ? { spine: grading as readonly SpineLevel[] }
    : ((grading as AestheticsGrading | null | undefined) ?? {});

/** Punctuation and case carry no meaning in a band label: "Fabric care - Sheets" is the value
 *  a promoted label spells "Fabric care Sheets". */
const searchKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, "");

/** The column a variant restriction is enforced on: the pogger matches it against a
 *  candidate's `variant`, which the store pog keeps as its CDT0. */
const VARIANT_ATTR = POG_CDT_COLUMNS[0];

const pushOnce = (index: Map<string, string[]>, key: string, skuId: string) => {
  const bucket = index.get(key);
  if (!bucket) index.set(key, [skuId]);
  else if (!bucket.includes(skuId)) bucket.push(skuId);
};

/** The SKUs whose variant (CDT0) is exactly each value. No other column is read: a brand
 *  column holding "Old Spice" does not make an "Old Spice Premium" product an "Old Spice" one. */
const skusByVariantOf = (items: readonly StudioItem[]): Map<string, string[]> => {
  const index = new Map<string, string[]>();
  for (const item of items) {
    const variant = item.attrs[VARIANT_ATTR]?.trim();
    if (variant) pushOnce(index, variant, item.skuId);
  }
  return index;
};

/**
 * Which SKUs a LEGACY fence label names: a fence stored on the pog, not a sent restriction.
 *
 * The variant exactly, when some product carries it. Older served plans also store PROMOTED
 * band labels — the label a merchant sees once a level was carved out of its parent, a JOINED
 * path such as "<variant> <cdt1>" that matches no single column. Only when the label is no
 * variant is it read as one: against each item's own path down the spine, starting AT the
 * variant, punctuation and case ignored. Never against a brand or any other column. A label
 * that matches nothing resolves to no SKUs and is reported as NOT GRADED rather than guessed at.
 */
const legacySkuResolverOf = (
  items: readonly StudioItem[],
  spine: readonly SpineLevel[],
): ((label: string) => string[]) => {
  const byVariant = skusByVariantOf(items);
  const byPath = new Map<string, string[]>();
  const attrs = [
    VARIANT_ATTR,
    ...spine.map((level) => level.attr).filter((attr) => attr !== VARIANT_ATTR),
  ];
  for (const item of items) {
    if (!item.attrs[VARIANT_ATTR]?.trim()) continue;
    let joined = "";
    for (const attr of attrs) {
      const value = item.attrs[attr];
      if (!value) continue;
      joined += searchKey(value);
      if (joined) pushOnce(byPath, joined, item.skuId);
    }
  }
  return (label) => byVariant.get(label.trim()) ?? byPath.get(searchKey(label)) ?? [];
};

/** One rule under construction: its cells gathered bay by bay as the shelves are walked. */
interface GatheredRule {
  value: string;
  type: PogShelfRestriction["type"];
  cells: Map<string, { run: number; shelf: number; bays: number[]; board?: number }>;
}

/**
 * The bands a served plan carries, read off THE SHELF THEY WERE FOUND ON.
 *
 * `sourceRule.shelfRule` is not consulted: a served three-shelf store plan can carry rules
 * whose text still names shelves four to six of the plan they were written for, so the only
 * trustworthy statement of where a rule applies is the shelf whose list holds it. Where one cell
 * holds several boards (a rack on its host shelf), a value is fenced there only if every board
 * fences it.
 */
export const bandsFromPogRestrictions = (
  restrictions: readonly PogShelfRestriction[],
  items: readonly StudioItem[],
  spine: readonly SpineLevel[],
): BandRule[] => {
  if (restrictions.length === 0) return [];
  const skusOf = legacySkuResolverOf(items, spine);
  const byRule = new Map<string, GatheredRule>();

  const fenceKeyOf = (fence: PogShelfRestriction) =>
    `${fence.run}|${fence.shelf}|${fence.bay}|${fence.value}`;
  const fencingBoards = new Map<string, Set<number | undefined>>();
  for (const fence of restrictions) {
    if (fence.type !== "MUST_NOT_HAVE") continue;
    const boards = fencingBoards.get(fenceKeyOf(fence)) ?? new Set();
    fencingBoards.set(fenceKeyOf(fence), boards.add(fence.board));
  }

  for (const restriction of restrictions) {
    if (
      restriction.type === "MUST_NOT_HAVE" &&
      fencingBoards.get(fenceKeyOf(restriction))!.size < (restriction.boards ?? 1)
    )
      continue;
    const id = `${restriction.type}␟${restriction.value}`;
    const entry: GatheredRule = byRule.get(id) ?? {
      value: restriction.value,
      type: restriction.type,
      cells: new Map(),
    };
    // DEV-3717: a MUST_HAVE on one board of a shared cell asks for the value on that board.
    const board =
      restriction.type === "MUST_HAVE" && (restriction.boards ?? 1) > 1
        ? restriction.board
        : undefined;
    const cellKey = `${restriction.run}|${restriction.shelf}${board === undefined ? "" : `|${restriction.bay}|${board}`}`;
    const cell = entry.cells.get(cellKey) ?? {
      run: restriction.run,
      shelf: restriction.shelf,
      bays: [],
      ...(board !== undefined && { board }),
    };
    if (!cell.bays.includes(restriction.bay)) cell.bays.push(restriction.bay);
    entry.cells.set(cellKey, cell);
    byRule.set(id, entry);
  }

  return [...byRule.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([id, entry]) => {
      const skuIds = skusOf(entry.value);
      return {
        id,
        condition: entry.type,
        label: entry.value,
        skuIds,
        resolved: skuIds.length > 0,
        cells: [...entry.cells.values()]
          .sort((a, b) => a.run - b.run || a.shelf - b.shelf)
          .map((cell) => ({
            run: cell.run,
            shelf: cell.shelf,
            bays: [...cell.bays].sort((a, b) => a - b),
            ...(cell.board !== undefined && { board: cell.board }),
          })),
      } satisfies BandRule;
    });
};

/**
 * The bands a store pog's solve was SENT, on the plan's own rows.
 *
 * One rule per condition and variant. Each sent shelf is its own cell (its bay and its row):
 * a MUST_HAVE asks for the variant on THAT shelf, so a facing in the next bay does not keep it.
 * A `may_have` list fences every variant the plan carries that the list leaves out. A value
 * governs exactly the products whose variant (CDT0) it is, placed or not. A shelf the plan
 * does not have is skipped. Where one cell holds several boards (a rack on its host shelf), a
 * value is fenced there only if every board fences it.
 */
export const bandsFromSentRestrictions = (
  sent: SentRestrictions,
  pog: Pog,
  items: readonly StudioItem[],
): BandRule[] => {
  const rowOf = pogShelfRowResolver(pog);
  const byVariant = skusByVariantOf(items);
  // Taken before unplaced SKUs join: a variant with no facings cannot break a may_have fence.
  const variants = [...byVariant.keys()];
  // A required variant whose SKUs were all left off must grade as missing, not as unresolved.
  for (const item of pog.unrangedItems) {
    const variant = item.variant?.trim();
    if (variant) pushOnce(byVariant, variant, item.productCode || item.id);
  }
  const byRule = new Map<
    string,
    {
      condition: BandRule["condition"];
      label: string;
      cells: Map<string, { run: number; shelf: number; bays: number[]; board?: number }>;
    }
  >();
  const add = (
    condition: BandRule["condition"],
    label: string,
    bay: number,
    shelf: number,
    board?: number,
  ) => {
    const id = `${condition}␟${label}`;
    const rule = byRule.get(id) ?? { condition, label, cells: new Map() };
    rule.cells.set(`${shelf}|${bay}|${board ?? ""}`, {
      run: 0,
      shelf,
      bays: [bay],
      ...(board !== undefined && { board }),
    });
    byRule.set(id, rule);
  };
  const fencedByCell = new Map<string, { bay: number; shelf: number; boards: Set<string>[] }>();
  for (const entry of sent.shelves) {
    const row = rowOf(entry.bay, entry.shelf, entry.fixtureId);
    if (!row) continue;
    // DEV-3717: a MUST_HAVE on one board of a shared cell asks for the value on that board.
    for (const value of entry.mustHave) {
      add("MUST_HAVE", value.trim(), row.bay, row.shelf, row.board);
    }
    const fenced = new Set(entry.mustNotHave.map((value) => value.trim()));
    if (entry.mayHave !== "*") {
      const allowed = new Set(entry.mayHave.map((value) => value.trim()));
      for (const variant of variants) if (!allowed.has(variant)) fenced.add(variant);
    }
    const key = `${row.shelf}|${row.bay}`;
    const cell = fencedByCell.get(key) ?? { bay: row.bay, shelf: row.shelf, boards: [] };
    cell.boards.push(fenced);
    fencedByCell.set(key, cell);
  }
  for (const { bay, shelf, boards } of fencedByCell.values()) {
    for (const value of boards[0]) {
      if (boards.every((fenced) => fenced.has(value))) add("MUST_NOT_HAVE", value, bay, shelf);
    }
  }
  return [...byRule.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([id, rule]) => {
      const skuIds = byVariant.get(rule.label) ?? [];
      return {
        id,
        condition: rule.condition,
        label: rule.label,
        skuIds,
        resolved: skuIds.length > 0,
        cells: [...rule.cells.values()].sort((a, b) => a.shelf - b.shelf || a.bays[0] - b.bays[0]),
      } satisfies BandRule;
    });
};

/** The bands and range the score grades a plan against: the draft's accepted restrictions,
 *  else the restrictions its solve was sent, else the fences stored on its shelves. A move gate
 *  measures with the same scope, or it approves a move that drops a rule or a SKU the score then
 *  charges for. */
export const aestheticsScopeOf = (
  pog: Pog,
  items: readonly StudioItem[],
  shelfRestrictions: readonly PogShelfRestriction[],
  draft: StudioDraft,
  restrictions?: SentRestrictions | null,
): AestheticsScope => {
  const bands: BandRule[] =
    draft.restrictions.length > 0
      ? draft.restrictions.map((restriction) => ({
          id: restriction.id,
          // Same kind-derived default the upload applies (see `AcceptedRestriction.condition`).
          condition:
            restriction.condition ??
            (restriction.kind === "split-off" || restriction.kind === "breakout"
              ? "CAN_HAVE"
              : "MUST_HAVE"),
          label: restriction.text || restriction.cdtPath.join(" "),
          skuIds: restriction.skuIds,
          resolved: restriction.skuIds.length > 0,
          cells: (restriction.placement?.cells ?? []).map((cell) => ({
            shelf: cell.shelf,
            bays: cell.bays,
          })),
        }))
      : restrictions
        ? bandsFromSentRestrictions(restrictions, pog, items)
        : bandsFromPogRestrictions(shelfRestrictions, items, draft.spine);

  // Unranged items are ranged SKUs the plan did not place.
  const rangedSkuIds = [
    ...items.map((item) => item.skuId),
    ...pog.unrangedItems.map((item) => item.productCode || item.id),
  ];
  return { bands, rangedSkuIds };
};

export const pogAestheticsScore = (
  pog: Pog | null,
  rules: Partial<SavedAestheticsRules> = {},
  options: PogAestheticsOptions = {},
): AestheticsScore | null => {
  if (!pog) return null;
  try {
    const { items, runs, shelfRestrictions } = pogToStudioItems([pog], options.adapter);
    if (!items.length || !runs.length) return null;

    /* A saved tree wins; otherwise the plan's own CDT columns. A plan whose columns name no
       block cannot be judged on blocking, the measure that matters most, so it is not scored
       rather than scored on what is left. */
    const columns = hierarchyFromColumns(items, options.columns);
    const orient = orientOf(options.blocking ?? "vertical");
    const spine: readonly SpineLevel[] = options.draft?.spine ??
      options.spine ?? [
        ...columns.levels.map((attr) => ({ attr, label: attr, orient })),
        { attr: "size", label: "Size", orient: "horizontal" as const },
      ];
    if (!options.draft && !options.spine && columns.levels.length === 0) return null;
    const draft: StudioDraft = options.draft ?? {
      spine,
      valueOrder: {},
      scopedValueOrder: {},
      bandOverrides: {},
      orientOverrides: {},
      shelfHints: {},
      attrOverrides: {},
      restrictions: [],
      dismissedFlagIds: [],
      appliedFixFlagIds: [],
    };
    const contract = options.draft || options.spine ? "finalized" : "columns";

    /* A draft that carries its own accepted restrictions states them itself. A store pog is
       graded on what its solve was sent. Only when neither is to hand do the fences stored on
       the pog's shelves stand in, and the result says it was graded on legacy fences. */
    const fromDraft = draft.restrictions.length > 0;
    const bandSource: BandSource = fromDraft
      ? "draft"
      : options.restrictions
        ? options.restrictions.source
        : "legacy fences";
    const { bands, rangedSkuIds } = aestheticsScopeOf(
      pog,
      items,
      shelfRestrictions,
      draft,
      options.restrictions,
    );
    /* The levels were picked skipping placeholder values ("Others", "Unassigned CDT2"), so the
       grading skips them too: a placeholder is not a block the plan was asked to keep. The
       unplaced items are read the same way, or an unplaced "Others" names a brand no placed
       item can carry and fails the no-brand-eliminated gate for a brand nobody dropped. */
    const unplacedAttrs = Object.fromEntries(
      pog.unrangedItems.map((item) => {
        const attrs: Record<string, string | null> = {
          subCategory: item.subCategory,
          brand: item.brand,
          cdt1: item.cdt1,
          cdt2: item.cdt2,
          cdt3: item.cdt3,
          variant: item.variant,
          size: item.size,
        };
        if (contract === "columns") {
          for (const attr of columns.levels) {
            if (namesNoBlock(attrs[attr])) attrs[attr] = null;
          }
        }
        return [item.productCode || item.id, attrs];
      }),
    );
    /* A release spine names the same pog columns, so its roles are read by name too: by
       position a one-level release makes size the brand, and every release makes size the
       flavour. A saved Studio draft keeps the positional roles it was built with. */
    const cdtColumns: readonly string[] = options.columns ?? POG_CDT_COLUMNS;
    const releaseLevels =
      !options.draft && options.spine
        ? options.spine.map((level) => level.attr).filter((attr) => cdtColumns.includes(attr))
        : [];
    const roles =
      contract === "columns"
        ? columnRoles(items, columns)
        : releaseLevels.length
          ? columnRoles(items, {
              levels: releaseLevels,
              leafAttr: leafBelow(items, cdtColumns, releaseLevels),
            })
          : {};
    const graded =
      contract === "columns"
        ? items.map((item) => ({
            ...item,
            attrs: {
              ...item.attrs,
              ...Object.fromEntries(columns.levels.map((attr) => [attr, blockValue(item, attr)])),
            },
          }))
        : items;
    return scorePlanogramAesthetics({
      items: graded,
      runs,
      draft,
      rules: { ...roles, ...rules },
      rangedSkuIds,
      unplacedAttrs,
      bands,
      bandSource,
      coreRange: options.coreRange ?? coreRangeFromPog(pog),
      contract,
      leafAttr: options.draft || options.spine ? undefined : columns.leafAttr,
    });
  } catch (error) {
    // A metric never breaks a page, but a plan left "Not scored" by a bug must be findable.
    console.warn(`Aesthetics score failed for plan ${options.planId ?? "(no id)"}`, error);
    return null;
  }
};

/**
 * A store pog graded as a page holding its CDT release grades it, and as its stored score is
 * graded (`Pog.calculateMetrics`): against the release's levels when it has any, else on the
 * plan's own columns oriented by the blocking its request sent, and against the restrictions and
 * core range its generation sent when given.
 */
export const releaseAestheticsScore = (
  pog: Pog | null,
  grading?: readonly SpineLevel[] | AestheticsGrading | null,
  planId?: string | number,
): AestheticsScore | null => {
  const { spine: releaseSpine, restrictions, coreRange, blocking, lengthUnit } = gradingOf(grading);
  const spine = releaseSpine ? spineOfRelease(releaseSpine) : null;
  return pogAestheticsScore(
    pog,
    lengthUnit ? { lengthUnit } : {},
    { planId, spine: spine ?? undefined, restrictions, coreRange, blocking },
  );
};
